<!--
    Favicon Generator Component
    - Custom element for generating favicon files from an image
    - Uses Canvas API and Jimp for client-side processing
    - Generates multiple PNG sizes and ICO file
    - Creates a downloadable ZIP package with all files
    - shadow: "none" disables Shadow DOM to inherit global Bulma styles
-->
<svelte:options customElement={{ tag: "favicon-generator", shadow: "none" }} />

<script lang="ts">
    import { FaviconGenerator } from "../../lib/favicon-generator";
    import type { ProcessingStatus, GeneratedFavicon } from "../../lib/favicon-generator";

    // ============================================================================
    // COMPONENT STATE
    // ============================================================================

    let status: ProcessingStatus = $state<ProcessingStatus>("idle");
    let selectedFile: File | null = $state(null);
    let previewUrl: string | null = $state(null);
    let isDragging: boolean = $state(false);
    let progress: number = $state(0);
    let errorMessage: string | null = $state(null);
    let generatedFavicons: GeneratedFavicon[] = $state([]);

    let fileInput: HTMLInputElement | undefined = $state();

    // ============================================================================
    // FILE HANDLING
    // ============================================================================

    function handleFileSelect(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files[0]) {
            selectFile(target.files[0]);
        }
    }

    function handleDrop(event: DragEvent): void {
        event.preventDefault();
        isDragging = false;

        if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
            selectFile(event.dataTransfer.files[0]);
        }
    }

    function handleDragOver(event: DragEvent): void {
        event.preventDefault();
        isDragging = true;
    }

    function handleDragLeave(): void {
        isDragging = false;
    }

    function selectFile(file: File): void {
        // Validate file type
        if (!file.type.startsWith("image/")) {
            errorMessage = "Please select a valid image file.";
            return;
        }

        // Clean up previous preview
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }

        selectedFile = file;
        previewUrl = URL.createObjectURL(file);
        status = "loading";
        errorMessage = null;
        generatedFavicons = [];
    }

    function openFilePicker(): void {
        fileInput?.click();
    }

    function clearSelection(): void {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        selectedFile = null;
        previewUrl = null;
        status = "idle";
        errorMessage = null;
        generatedFavicons = [];
        progress = 0;

        // Reset file input
        if (fileInput) {
            fileInput.value = "";
        }
    }

    // ============================================================================
    // FAVICON GENERATION
    // ============================================================================

    async function generateFavicons(): Promise<void> {
        if (!selectedFile || status === "processing") {
            return;
        }

        status = "processing";
        errorMessage = null;
        progress = 0;

        try {
            // Generate all favicons
            generatedFavicons = await FaviconGenerator.generateAllFavicons(selectedFile, (progressValue) => {
                progress = progressValue;
            });

            status = "completed";
        } catch (error) {
            status = "error";
            errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            generatedFavicons = [];
        }
    }

    async function downloadZip(): Promise<void> {
        if (generatedFavicons.length === 0) {
            return;
        }

        try {
            const zipBlob = await FaviconGenerator.createZipFile(generatedFavicons);
            FaviconGenerator.downloadBlob(zipBlob, "favicons.zip");
        } catch (error) {
            status = "error";
            errorMessage = error instanceof Error ? error.message : "Failed to create ZIP file";
        }
    }

    // ============================================================================
    // COMPUTED VALUES
    // ============================================================================

    const showPreview = $derived(previewUrl !== null);
    const isProcessing = $derived(status === "processing");
    const isCompleted = $derived(status === "completed");
    const hasError = $derived(status === "error");
</script>

<!-- ============================================================================ -->
<!-- COMPONENT TEMPLATE -->
<!-- ============================================================================ -->

<div class="favicon-generator">
    <!-- Privacy Notification -->
    <div class="notification is-info is-light mb-4">
        <p>
            <strong><i class="fas fa-shield-alt mr-2"></i>Privacy Notice:</strong>
            All image processing is performed entirely in your browser. No images are uploaded to our servers. Your images remain 100% private.
        </p>
    </div>

    <!-- Upload Area -->
    {#if !showPreview}
        <div class="upload-area box {isDragging ? 'is-dragging' : ''}" ondrop={handleDrop} ondragover={handleDragOver} ondragleave={handleDragLeave} onclick={openFilePicker} onkeydown={(e) => e.key === "Enter" && openFilePicker()} role="button" tabindex="0">
            <input type="file" bind:this={fileInput} onchange={handleFileSelect} accept="image/*" style="display: none;" />

            <div class="upload-prompt">
                <p class="icon is-large">
                    <i class="fas fa-cloud-upload-alt fa-3x"></i>
                </p>
                <p class="is-size-5 has-text-weight-semibold">Drop your image here or click to select</p>
                <p class="is-size-7 has-text-grey mt-2">Supports PNG, JPEG, WebP, BMP, TIFF, GIF, and SVG formats</p>
                <p class="is-size-7 has-text-grey">Images will be cropped to square automatically</p>
            </div>
        </div>
    {:else}
        <!-- Preview and Controls -->
        <div class="box">
            <div class="columns">
                <div class="column is-one-third">
                    <div class="preview-container">
                        <img src={previewUrl} alt="Preview" class="preview-image" />
                        <p class="is-size-7 has-text-grey has-text-centered mt-2">
                            {selectedFile?.name}
                        </p>
                    </div>
                </div>
                <div class="column">
                    <div class="content">
                        {#if status === "loading"}
                            <h3 class="title is-5">Ready to Generate</h3>
                            <p class="mb-4">Click the button below to generate your favicon package.</p>
                            <div class="buttons">
                                <button class="button is-primary" onclick={generateFavicons}>
                                    <span class="icon">
                                        <i class="fas fa-magic"></i>
                                    </span>
                                    <span>Generate Favicons</span>
                                </button>
                                <button class="button is-light" onclick={clearSelection}>
                                    <span class="icon">
                                        <i class="fas fa-times"></i>
                                    </span>
                                    <span>Cancel</span>
                                </button>
                            </div>
                        {:else if isProcessing}
                            <h3 class="title is-5">Processing...</h3>
                            <progress class="progress is-primary" value={progress} max="100">
                                {progress}%
                            </progress>
                            <p class="is-size-7 has-text-grey">
                                Generating favicon files: {Math.round(progress)}% complete
                            </p>
                        {:else if isCompleted}
                            <h3 class="title is-5">
                                <span class="icon has-text-success">
                                    <i class="fas fa-check-circle"></i>
                                </span>
                                Generation Complete!
                            </h3>
                            <p class="mb-4">Your favicon package is ready to download.</p>
                            <div class="box has-background-light mb-4">
                                <p class="has-text-weight-semibold mb-2">Package includes:</p>
                                <ul class="is-size-7">
                                    <li>favicon.ico (32×32)</li>
                                    <li>favicon-16x16.png</li>
                                    <li>favicon-32x32.png</li>
                                    <li>apple-touch-icon.png (180×180)</li>
                                    <li>android-chrome-192x192.png</li>
                                    <li>android-chrome-512x512.png</li>
                                    <li>site.webmanifest</li>
                                </ul>
                            </div>
                            <div class="buttons">
                                <button class="button is-primary" onclick={downloadZip}>
                                    <span class="icon">
                                        <i class="fas fa-download"></i>
                                    </span>
                                    <span>Download ZIP</span>
                                </button>
                                <button class="button is-light" onclick={clearSelection}>
                                    <span class="icon">
                                        <i class="fas fa-redo"></i>
                                    </span>
                                    <span>Generate Another</span>
                                </button>
                            </div>
                        {:else if hasError}
                            <h3 class="title is-5">
                                <span class="icon has-text-danger">
                                    <i class="fas fa-exclamation-triangle"></i>
                                </span>
                                Error
                            </h3>
                            <div class="notification is-danger is-light">
                                <p>{errorMessage}</p>
                            </div>
                            <div class="buttons">
                                <button class="button is-primary" onclick={generateFavicons}>
                                    <span class="icon">
                                        <i class="fas fa-redo"></i>
                                    </span>
                                    <span>Try Again</span>
                                </button>
                                <button class="button is-light" onclick={clearSelection}>
                                    <span class="icon">
                                        <i class="fas fa-times"></i>
                                    </span>
                                    <span>Cancel</span>
                                </button>
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <!-- Information Section -->
    <div class="box mt-5">
        <h3 class="title is-5">How to Use Your Favicons</h3>
        <div class="content">
            <p>After downloading your favicon package, follow these steps to add them to your website:</p>
            <ol>
                <li>Extract the ZIP file to your website's root directory</li>
                <li>Add the following code to the <code>&lt;head&gt;</code> section of your HTML:</li>
            </ol>
            <pre class="has-background-light p-4"><code
                    >&lt;link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png"&gt;
&lt;link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png"&gt;
&lt;link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png"&gt;
&lt;link rel="manifest" href="/site.webmanifest"&gt;</code
                ></pre>
            <p class="mt-3">
                <strong>Note:</strong> Most browsers will automatically detect <code>favicon.ico</code> in your root directory, but it's recommended to include the PNG versions as well for better quality and broader compatibility.
            </p>
        </div>
    </div>

    <!-- Tips Section -->
    <div class="box">
        <h3 class="title is-5">Tips for Best Results</h3>
        <div class="content">
            <ul>
                <li><strong>Image Size:</strong> Use an image at least 512×512 pixels for best quality</li>
                <li><strong>Simple Designs:</strong> Favicons are displayed at small sizes, so simple, bold designs work best</li>
                <li><strong>Square Images:</strong> If your image isn't square, it will be cropped automatically (portrait: top/bottom clipped, landscape: left/right clipped)</li>
                <li><strong>Transparent Backgrounds:</strong> PNG images with transparent backgrounds are supported</li>
                <li><strong>Testing:</strong> After uploading, test your favicons in different browsers and devices</li>
            </ul>
        </div>
    </div>
</div>

<!-- ============================================================================ -->
<!-- COMPONENT STYLES -->
<!-- ============================================================================ -->

<style>
    .favicon-generator {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .upload-area {
        border: 2px dashed #dbdbdb;
        border-radius: 6px;
        padding: 3rem;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
        background-color: #fafafa;
    }

    .upload-area:hover {
        border-color: #b5b5b5;
        background-color: #f0f0f0;
    }

    .upload-area.is-dragging {
        border-color: #3273dc;
        background-color: #eef6fc;
    }

    .upload-prompt {
        color: #7a7a7a;
    }

    .preview-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .preview-image {
        max-width: 100%;
        max-height: 300px;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    pre {
        border-radius: 4px;
        overflow-x: auto;
    }

    code {
        font-size: 0.85em;
    }
</style>
